import {
  DestroyRef,
  type EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';

/**
 * Reports a GA4 `page_view` on every completed navigation, tagged with the
 * resolved route title. The title is read from the router snapshot (already
 * populated at `NavigationEnd`) rather than `document.title`, which the router
 * only updates after the event fires — reading the DOM here would log the
 * previous page's title. Without a route title, a title set through
 * `GoogleAnalyticsService.setPageTitle` wins over `document.title`.
 */
export function provideGoogleAnalyticsPageTracking(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer((): void => {
      const router = inject(Router);
      const ga = inject(GoogleAnalyticsService);
      const destroyRef = inject(DestroyRef);

      router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe((): void => {
          let route = router.routerState.snapshot.root;
          while (route.firstChild) {
            route = route.firstChild;
          }
          ga.trackPage(window.location.href, route.title ?? ga.pageTitle() ?? document.title);
        });
    }),
  ]);
}
